import { SchedulerConfigurationData } from '../../../../../api';

export interface ResourceCalculation {
  resourcesNeeded: number;
  selectedResources: number[] | null;
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Calculate the number of resources needed based on persons and capacity per resource
 */
export const calculateRequiredResourceCount = (persons: number, personsPerResource: number): number => {
  if (persons <= 0 || personsPerResource <= 0) return 0;
  return Math.ceil(persons / personsPerResource);
};

/**
 * Calculate which resources should be selected based on starting resource and requirements
 */
/**
 * Calculate adjacent resource selection for configurations that require adjacent resources
 */
const calculateAdjacentResourceSelection = (
  startingResourceIndex: number,
  resourcesNeeded: number,
  availableResourceIndexes: number[],
  config: SchedulerConfigurationData,
): ResourceCalculation => {
  const selectedResources: number[] = [];

  // Check if we can fit required adjacent resources from starting point
  for (let i = 0; i < resourcesNeeded; i++) {
    const resourceIndex = startingResourceIndex + i;

    // Check bounds
    if (resourceIndex >= config.resource_count) {
      return {
        resourcesNeeded,
        selectedResources: null,
        isValid: false,
        errorMessage: 'Not enough adjacent resources available from this starting position',
      };
    }

    // Check availability
    if (!availableResourceIndexes.includes(resourceIndex)) {
      return {
        resourcesNeeded,
        selectedResources: null,
        isValid: false,
        errorMessage: `Resource ${resourceIndex + 1} is not available`,
      };
    }

    selectedResources.push(resourceIndex);
  }

  return {
    resourcesNeeded,
    selectedResources,
    isValid: true,
  };
};

/**
 * Calculate flexible resource selection for configurations that don't require adjacent resources
 */
const calculateFlexibleResourceSelection = (
  startingResourceIndex: number,
  resourcesNeeded: number,
  availableResourceIndexes: number[],
): ResourceCalculation => {
  // For flexible selection, we start with the selected resource
  // and let the user choose additional resources manually

  if (!availableResourceIndexes.includes(startingResourceIndex)) {
    return {
      resourcesNeeded,
      selectedResources: null,
      isValid: false,
      errorMessage: `Resource ${startingResourceIndex + 1} is not available`,
    };
  }

  return {
    resourcesNeeded,
    selectedResources: [startingResourceIndex],
    isValid: true,
  };
};

export const calculateResourceSelection = (
  startingResourceIndex: number,
  persons: number,
  config: SchedulerConfigurationData,
  availableResourceIndexes: number[],
): ResourceCalculation => {
  const resourcesNeeded = calculateRequiredResourceCount(persons, config.persons_per_resource);

  if (resourcesNeeded === 0) {
    return {
      resourcesNeeded: 0,
      selectedResources: [],
      isValid: true,
    };
  }

  if (config.require_adjacent_resources) {
    return calculateAdjacentResourceSelection(startingResourceIndex, resourcesNeeded, availableResourceIndexes, config);
  }
  return calculateFlexibleResourceSelection(startingResourceIndex, resourcesNeeded, availableResourceIndexes);
};

/**
 * Validate a manual resource selection
 */
export const validateResourceSelection = (
  selectedResourceIds: number[],
  persons: number,
  config: SchedulerConfigurationData,
  availableResourceIndexes: number[],
): { isValid: boolean; errorMessage?: string } => {
  const resourcesNeeded = calculateRequiredResourceCount(persons, config.persons_per_resource);

  // Check if we have enough resources
  if (selectedResourceIds.length < resourcesNeeded) {
    return {
      isValid: false,
      errorMessage: `Need ${resourcesNeeded} resources for ${persons} persons, but only ${selectedResourceIds.length} selected`,
    };
  }

  // Check if all selected resources are available
  const unavailableResources = selectedResourceIds.filter((id) => !availableResourceIndexes.includes(id));

  if (unavailableResources.length > 0) {
    return {
      isValid: false,
      errorMessage: `Resources ${unavailableResources.map((id) => id + 1).join(', ')} are not available`,
    };
  }

  // Check adjacency requirement
  if (config.require_adjacent_resources && selectedResourceIds.length > 1) {
    const sortedIds = [...selectedResourceIds].sort((a, b) => a - b);
    for (let i = 1; i < sortedIds.length; i++) {
      if (sortedIds[i] !== sortedIds[i - 1] + 1) {
        return {
          isValid: false,
          errorMessage: 'Selected resources must be adjacent',
        };
      }
    }
  }

  return { isValid: true };
};

/**
 * Check if a starting resource position would allow for a valid selection
 */
export const isValidStartingPosition = (
  resourceIndex: number,
  persons: number,
  config: SchedulerConfigurationData,
  availableResourceIndexes: number[],
): boolean => {
  const calculation = calculateResourceSelection(resourceIndex, persons, config, availableResourceIndexes);

  return calculation.isValid && calculation.selectedResources !== null;
};
